"use client";

import { useEffect, useState } from "react";
import DOMPurify from "dompurify";

const assets = `https://assetsvilva.blr1.cdn.digitaloceanspaces.com/ecomsaas/redisapi/${process.env.NEXT_PUBLIC_STORE_CODE}`;

interface DynamicBlogsProps {
  pageslug: string;
  pagetitle: string;
  pagecontent: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  relatedPosts: any[];
  image: string;
  category_id?: number | null;
}

const DynamicBlogs = ({
  pageslug,
  pagetitle,
  pagecontent,
  meta_title,
  meta_description,
  meta_keywords,
  relatedPosts,
  image,
  category_id,
}: DynamicBlogsProps) => {
  const [sanitizedContent, setSanitizedContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Function to strip HTML tags (for short descriptions)
  function stripHTMLTags(html: string) {
    if (!html) return "No description available.";
    return html.replace(/<[^>]+>/g, "") || "No description available.";
  }

  // Function to decode HTML entities
  function decodeHtmlEntities(encodedString: string) {
    if (typeof window === "undefined") {
      return encodedString;
    }
    const textArea = document.createElement("textarea");
    textArea.innerHTML = encodedString;
    return textArea.value;
  }

  // Function to clean syntax highlighting and unwanted markup
  function cleanSyntaxHighlighting(html: string) {
    if (!html) return "";
    return html
      .replace(/<span style="color: rgb\([^)]+\);">(.*?)<\/span>/g, "$1")
      .replace(/<span style="color: rgb\([^)]+\);">/g, "")
      .replace(/<\/span>/g, "")
      .replace(/<p>```html<\/p>/g, "")
      .replace(/<p><br><\/p>/g, "")
      .replace(/<header[\s\S]*?<\/header>/g, "")
      .replace(/<svg[\s\S]*?<\/svg>/g, "")
      .replace(/<button[\s\S]*?<\/button>/g, "")
      .replace(/<nav[\s\S]*?<\/nav>/g, "")
      .replace(/<div class="[^"]*header[^"]*"[\s\S]*?(?=<\/div>)/g, "")
      .replace(/<div class="[^"]*nav[^"]*"[\s\S]*?(?=<\/div>)/g, "")
      .replace(/\[&amp;_svg\][^\]]*]/g, "")
      .replace(/<[^>]+class="lucide[^>]+>/g, "")
      .replace(/&amp;/g, "&")
      .replace(/<!DOCTYPE[\s\S]*?>/g, "") // Remove DOCTYPE
      .replace(/<html[\s\S]*?>|<\/html>/g, "") // Remove html tags
      .replace(/<body[\s\S]*?>|<\/body>/g, ""); // Remove body tags
  }

  // Clean non-HTML content and unrelated markup
  function cleanNonHtmlContent(content: string) {
    if (!content) return "";
    // Split content into lines and find valid HTML content
    const lines = content.split("\n");
    const htmlStartIndex = lines.findIndex((line) =>
      line.trim().match(/^<(div|h1|h2|h3|p|img)[\s>]/)
    );
    let cleaned = htmlStartIndex !== -1 ? lines.slice(htmlStartIndex).join("\n") : content;

    // Remove non-HTML lines and stray markup
    cleaned = cleaned
      .replace(/^[^\<].*$/gm, "") // Remove lines not starting with <
      .replace(/\[&amp;_svg\][^\]]*]/g, "")
      .replace(/<[^>]+class="lucide[^>]+>/g, "")
      .trim();

    // Ensure content starts with a valid container
    if (!cleaned.startsWith("<div") && !cleaned.startsWith("<h") && !cleaned.startsWith("<p") && !cleaned.startsWith("<img")) {
      cleaned = `<div>${cleaned}</div>`;
    }

    return cleaned;
  }

  // Process content for initial render (server-side or static)
  const initialContent = (() => {
    try {
      const cleanedNonHtml = cleanNonHtmlContent(pagecontent);
      const cleanedContent = cleanSyntaxHighlighting(cleanedNonHtml);
      const decodedContent = decodeHtmlEntities(cleanedContent);
      // If on server or static rendering, return cleaned content without sanitization
      if (typeof window === "undefined") {
        return decodedContent || "<p>No content available.</p>";
      }
      // Client-side sanitization
      const sanitized = DOMPurify.sanitize(decodedContent, {
        ADD_TAGS: ["div", "h1", "h2", "h3", "p", "img", "span"],
        ADD_ATTR: ["style", "class", "alt", "src"],
      });
      return sanitized || "<p>No content available.</p>";
    } catch (error) {
      console.error("Error processing initial content:", error);
      return "<p>Failed to load content. Please try again.</p>";
    }
  })();

  // Client-side processing for dynamic updates
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsLoading(true);
      setError(null);
      try {
        const cleanedNonHtml = cleanNonHtmlContent(pagecontent);
        const cleanedContent = cleanSyntaxHighlighting(cleanedNonHtml);
        const decodedContent = decodeHtmlEntities(cleanedContent);
        const sanitized = DOMPurify.sanitize(decodedContent, {
          ADD_TAGS: ["div", "h1", "h2", "h3", "p", "img", "span"],
          ADD_ATTR: ["style", "class", "alt", "src"],
        });
        setSanitizedContent(sanitized || "<p>No content available.</p>");
        setIsLoading(false);
      } catch (error) {
        console.error("Error processing content:", error);
        setError("Failed to load content. Please try again.");
        setIsLoading(false);
      }
    }
  }, [pagecontent]);

  useEffect(() => {
    console.log("DynamicBlogs Props:", {
      pageslug,
      pagetitle,
      pagecontent,
      meta_title,
      meta_description,
      meta_keywords,
      relatedPosts,
      image,
      category_id,
    });
  }, [pageslug, pagetitle, pagecontent, meta_title, meta_description, meta_keywords, relatedPosts, image, category_id]);

  if (!pagecontent) {
    return <div>Blog post not found.</div>;
  }

  // Filter related posts by category_id
  const filteredRelatedPosts = relatedPosts.filter((post) => post.category_id === category_id);
  const postsToShow = filteredRelatedPosts.length > 0 ? filteredRelatedPosts : relatedPosts;
  const sectionTitle = filteredRelatedPosts.length > 0 ? "Related Posts" : "Other Blogs";

  return (
    <div className="lg:!container mx-auto !container-none md:!px-[20px] !px-[16px]">
      <div className="!flex lg:!flex-row md:!flex-col sm:!flex-col !flex-col !my-[20px]">
        <div className="detailed_con_blog lg:!w-8/12 md:!w-full sm:!w-full !w-full lg:!p-[20px] md:!p-[10px] sm:!p-[0px]">
          <h1 className="text-2xl font-bold mb-4">{decodeHtmlEntities(pagetitle)}</h1>
          <div
            style={{ marginTop: "10px" }}
            className="blog-content"
            dangerouslySetInnerHTML={{
              __html: error
                ? `<p>${error}</p>`
                : isLoading
                  ? initialContent // Use initial content to avoid "Loading content..." in page source
                  : sanitizedContent || "<p>No content available.</p>",
            }}
          />
        </div>

        <div className="lg:!w-4/12 md:!w-full sm:!w-full !w-full lg:!p-[20px] md:!p-[10px] sm:!p-[0px]">
          <div className="lg:!sticky lg:!top-[100px] lg:max-h-[80vh] lg:!overflow-y-auto md:!p-[10px] sm:!p-[0px]">
            <div>
              <p className="text-[#000] font-montserrat !text-[20px] not-italic !font-semibold leading-[26px]">
                {sectionTitle}
              </p>
            </div>
            <div className="!my-[20px]">
              {postsToShow.length > 0 ? (
                <div className="!grid lg:!grid-cols-1 md:!grid-cols-2 sm:!grid-cols-2 !grid-cols-1 !gap-x-8">
                  {postsToShow.slice(0, 3).map((item: any, index: number) => (
                    <div key={index} className="detailed_subblog pb-2 mb-3">
                      <div className="!bg-white !rounded-[16px] !shadow-[0px_0px_10px_0px_rgba(0,0,0,0.2)] !p-[4px]">
                        <a href={`/blog/${item.seo_url || item.slug || item.id}`} className="text-primary">
                          <div className="blog_card p-3 border rounded !space-y-[16px]">
                            <div className="blog_list_img relative">
                              <img
                                src={
                                  item.image
                                    ? `${assets}/${item.image}`
                                    : "/ecomsaas-assets/images/no-image.webp"
                                }
                                alt={item.title || "Recent"}
                                className="w-full h-[200px] object-top object-cover rounded-md"
                                onError={(e) => {
                                  e.currentTarget.src = "/ecomsaas-assets/images/no-image.webp";
                                }}
                              />
                            </div>
                            <div>
                              <p className="text-[20px] font-[700] text-black">
                                {decodeHtmlEntities(item.title) || "No Title Available"}
                              </p>
                            </div>
                            <div>
                              <p className="!text-[18px] !leading-[32px] !font-[500] !text-black font-montserrat not-italic">
                                {stripHTMLTags(item.short_content)?.length > 160
                                  ? stripHTMLTags(item.short_content).slice(0, 160) + "..."
                                  : stripHTMLTags(item.short_content) || "No description available."}
                              </p>
                            </div>
                            <div>
                              <p className="!text-[14px] !font-[700] !text-[#1734AB] font-montserrat not-italic !leading-none">
                                {item.created_at
                                  ? new Date(item.created_at).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })
                                  : "No Date Available"}
                              </p>
                            </div>
                          </div>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No {sectionTitle.toLowerCase()} available.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicBlogs;